import { Injectable, inject } from '@angular/core';
import { Subject } from 'rxjs';
import axios from 'axios';
import { environment } from 'src/environments/environment';
import { ClientsService } from '../clients/clients.service';

export type ClientCreditHistoryItem = {
  date: string | null;
  label: string;
  amount: string | null;
  filters: Record<string, string> | null;
};

export type ClientCreditTableItem = {
  product_label: string;
  fee_label: string;
  amount: number;
  currency: string;
};

type ClientCreditHistory = {
  history: ClientCreditHistoryItem[];
};

type GetCreditTableParams = {
  from: string;
  to: string;
};

export type ClientCreditTransferItem = {
  id: string;
  amount: number;
  currency_iso: string;
  state: string;
  created_at: string | null;
};

type ClientCreditTransfer = {
  transfers: ClientCreditTransferItem[];
};

type BuyCreditWithTransferParams = {
  amount: number;
  currency_iso: string;
};

type BuyCreditWithCardParams = {
  amount: number;
};

type BuyCreditWithCardResponse = {
  url: string;
};

@Injectable({
  providedIn: 'root'
})
export class CreditsService {
  private clientsService = inject(ClientsService);

  readonly refreshTransferHistory = new Subject<void>();

  async getCreditTransfers(clientId = this.clientsService.selectedClientId): Promise<ClientCreditTransferItem[]> {
    if (!clientId) {
      return [];
    }

    const result = await axios.get<ClientCreditTransfer>(`${environment.baseUrl}/api/client/${clientId}/credit-transfers`);

    return result.data.transfers;
  }

  async getCreditHistory(params: GetCreditTableParams, clientId = this.clientsService.selectedClientId): Promise<ClientCreditHistoryItem[]> {
    if (!clientId) {
      return [];
    }

    const result = await axios.get<ClientCreditHistory>(
      `${environment.baseUrl}/api/client/${clientId}/credit-history`,
      {
        params,
      }
    );

    return result.data.history;
  }

  async getCreditTable(params: GetCreditTableParams, clientId = this.clientsService.selectedClientId): Promise<ClientCreditTableItem[]> {
    if (!clientId) {
      return [];
    }

    const result = await axios.get<{ data: ClientCreditTableItem[]; }>(
      `${environment.baseUrl}/api/client/${clientId}/credit-table`,
      {
        params,
      }
    );

    return result.data.data;
  }

  async cancelCreditPurchase(creditTransferId: string, clientId = this.clientsService.selectedClientId): Promise<void> {
    await axios.post(`${environment.baseUrl}/api/client/${clientId}/cancel-credit-purchase/${creditTransferId}`);
  }

  async buyWithTransfer(params: BuyCreditWithTransferParams, clientId = this.clientsService.selectedClientId): Promise<string> {
    const result = await axios.post<{ credit_transfer_id: string; }>(
      `${environment.baseUrl}/api/client/${clientId}/buy-with-transfer`,
      params,
    );

    return result.data.credit_transfer_id;
  }

  async buyWithCard(params: BuyCreditWithCardParams, clientId = this.clientsService.selectedClientId): Promise<void> {
    const result = await axios.post<BuyCreditWithCardResponse>(
      `${environment.baseUrl}/api/client/${clientId}/buy-with-card`,
      params,
    );

    window.location.href = (result.data.url);
  }
}
